import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
    ArrowLeft,
    Sparkles,
    AlertTriangle,
    CheckCircle2,
    Clock,
    RefreshCw,
    Mail,
    Phone,
    MapPin,
    Cake,
    Briefcase,
    Send,
    Save,
    Loader2,
    StickyNote,
    Activity,
    HeartPulse,
    Apple,
    Pill,
    Brain,
    Users,
    FileText,
    ChevronDown,
    ChevronUp,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

const STATUS_OPTIONS = [
    { value: "new", label: "New" },
    { value: "in-review", label: "In review" },
    { value: "completed", label: "Completed" },
    { value: "archived", label: "Archived" },
];

const CATEGORY_ICON = {
    Diet: Apple,
    Lifestyle: Activity,
    Herbs: Sparkles,
    Bodywork: HeartPulse,
    Sleep: Brain,
    Other: Sparkles,
};

const Section = ({ icon: Icon, title, children, defaultOpen = true }) => {
    const [open, setOpen] = useState(defaultOpen);
    return (
        <div className="rounded-2xl border border-border/80 bg-white/90">
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="w-full flex items-center justify-between p-4 sm:p-5 text-left"
            >
                <div className="flex items-center gap-2">
                    {Icon && <Icon className="h-5 w-5 text-moss" strokeWidth={1.5} />}
                    <h3 className="font-display text-lg text-moss">{title}</h3>
                </div>
                {open ? (
                    <ChevronUp className="h-4 w-4 text-foreground/55" />
                ) : (
                    <ChevronDown className="h-4 w-4 text-foreground/55" />
                )}
            </button>
            {open && (
                <div className="px-4 sm:px-5 pb-5 text-sm text-foreground/85">
                    {children}
                </div>
            )}
        </div>
    );
};

const KV = ({ label, value }) => {
    if (value === null || value === undefined || value === "" || (Array.isArray(value) && value.length === 0)) {
        return null;
    }
    return (
        <div className="py-1">
            <span className="text-xs text-foreground/55 uppercase tracking-wider font-accent">
                {label}
            </span>
            <p className="text-sm text-foreground/85 whitespace-pre-wrap">
                {Array.isArray(value) ? value.join(", ") : String(value)}
            </p>
        </div>
    );
};

const SubObject = ({ obj }) => {
    if (!obj || typeof obj !== "object") return null;
    return (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6">
            {Object.entries(obj).map(([k, v]) => (
                <KV
                    key={k}
                    label={k.replaceAll("_", " ")}
                    value={
                        typeof v === "object" && v !== null && !Array.isArray(v)
                            ? JSON.stringify(v)
                            : v
                    }
                />
            ))}
        </div>
    );
};

const TableList = ({ rows, cols }) => {
    if (!rows || rows.length === 0) {
        return <p className="text-sm text-foreground/55">None.</p>;
    }
    return (
        <div className="overflow-x-auto rounded-xl border border-border/70">
            <table className="w-full text-sm">
                <thead className="bg-sand/40 text-left text-xs uppercase tracking-wider text-foreground/65">
                    <tr>
                        {cols.map((c) => (
                            <th key={c.key} className="p-2.5">
                                {c.label}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody className="divide-y divide-border/70">
                    {rows.map((r, i) => {
                        // Prefer a stable id; fall back to index only when none exists.
                        const rowKey = r?.id || r?._id || r?.name || `row-${i}`;
                        return (
                            <tr key={rowKey}>
                                {cols.map((c) => (
                                    <td
                                        key={`${rowKey}-${c.key}`}
                                        className="p-2.5 text-foreground/85"
                                    >
                                        {r[c.key] || "\u2014"}
                                    </td>
                                ))}
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};

// Module-level column definitions \u2014 stable references avoid re-renders.
const MEDICATIONS_COLS = [
    { key: "substance", label: "Substance" },
    { key: "type", label: "Type" },
    { key: "dosage", label: "Dosage" },
    { key: "purpose", label: "Purpose" },
    { key: "prescribed_by", label: "Prescribed by" },
    { key: "how_long", label: "How long" },
];

const SYMPTOM_COLS = [
    { key: "name", label: "Symptom" },
    { key: "frequency", label: "Freq" },
    { key: "intensity", label: "Intensity" },
];

const CHALLENGING_GROUPS = [
    ["Digestion", "digestion"],
    ["Elimination", "elimination"],
    ["Emotions", "emotions"],
    ["Other symptoms", "other_symptoms"],
    ["Diagnosed conditions", "diagnosed_conditions"],
];

export default function AdminIntakeDetailPage() {
    const { id } = useParams();
    const { authedFetch } = useAuth();
    const navigate = useNavigate();

    const [doc, setDoc] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [noteText, setNoteText] = useState("");
    const [adding, setAdding] = useState(false);
    const [regen, setRegen] = useState(false);
    const [resync, setResync] = useState(false);
    const [savingStatus, setSavingStatus] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await authedFetch(`/admin/intakes/${id}`);
            setDoc(data);
        } catch (e) {
            setError(
                e?.response?.status === 404
                    ? "This intake could not be found."
                    : e?.response?.data?.detail || "Failed to load intake.",
            );
        } finally {
            setLoading(false);
        }
    }, [authedFetch, id]);

    useEffect(() => {
        load();
    }, [load]);

    const intake = doc?.intake || {};
    const ai = doc?.ai || {};
    const ghl = doc?.ghl || {};
    const notes = doc?.notes_history || [];

    // Memoized derived values: avoid re-allocating arrays/strings on every render.
    const locationText = useMemo(
        () => [intake.city, intake.state].filter(Boolean).join(", "),
        [intake.city, intake.state],
    );

    const notesNewestFirst = useMemo(
        () => [...notes].reverse(),
        [notes],
    );

    const demographicsObj = useMemo(
        () => ({
            first_name: intake.first_name,
            last_name: intake.last_name,
            email: intake.email,
            phone: intake.phone,
            birthdate: intake.birthdate,
            age: intake.age,
            sex_assigned_at_birth: intake.sex_assigned_at_birth,
            gender_identity: intake.gender_identity,
            occupation: intake.occupation,
            marital_status: intake.marital_status,
            children_count: intake.children_count,
            children_ages: intake.children_ages,
            address_line1: intake.address_line1,
            city: intake.city,
            state: intake.state,
            postal_code: intake.postal_code,
        }),
        [intake],
    );

    const addNote = async () => {
        if (!noteText.trim()) {
            toast.error("Note cannot be empty.");
            return;
        }
        setAdding(true);
        try {
            await authedFetch(`/admin/intakes/${id}/notes`, {
                method: "POST",
                data: { note: noteText.trim() },
            });
            toast.success("Note saved.");
            setNoteText("");
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to add note.");
        } finally {
            setAdding(false);
        }
    };

    const updateStatus = async (newStatus) => {
        setSavingStatus(true);
        try {
            await authedFetch(`/admin/intakes/${id}/status`, {
                method: "PATCH",
                data: { status: newStatus },
            });
            toast.success(`Marked as ${newStatus}.`);
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to update status.");
        } finally {
            setSavingStatus(false);
        }
    };

    const regenAi = async () => {
        setRegen(true);
        try {
            await authedFetch(`/admin/intakes/${id}/regenerate-ai`, {
                method: "POST",
            });
            toast.success("AI summary regenerated.");
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "AI regeneration failed.");
        } finally {
            setRegen(false);
        }
    };

    const resyncGhl = async () => {
        setResync(true);
        try {
            await authedFetch(`/admin/intakes/${id}/resync-ghl`, {
                method: "POST",
            });
            toast.success("GHL re-sync started.");
            setTimeout(load, 3000);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "GHL re-sync failed.");
        } finally {
            setResync(false);
        }
    };

    if (loading) {
        return (
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-10 text-foreground/55">
                Loading intake...
            </div>
        );
    }
    if (error) {
        return (
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-10">
                <p className="text-terracotta">{error}</p>
                <Link
                    to="/admin"
                    className="mt-4 inline-flex items-center gap-1 text-moss hover:text-terracotta text-sm"
                >
                    <ArrowLeft className="h-4 w-4" /> Back to dashboard
                </Link>
            </div>
        );
    }

    return (
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8">
            <Link
                to="/admin"
                className="inline-flex items-center gap-1 text-sm text-moss hover:text-terracotta"
            >
                <ArrowLeft className="h-4 w-4" /> Back to dashboard
            </Link>

            {/* Header */}
            <div className="mt-4 rounded-3xl border border-border/80 bg-white/90 p-6 sm:p-8">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                    <div>
                        <h1
                            data-testid="admin-intake-name"
                            className="font-display text-3xl text-moss"
                        >
                            {intake.first_name} {intake.last_name}
                        </h1>
                        <p className="mt-1 text-sm text-foreground/65">
                            Submitted {new Date(doc.submitted_at).toLocaleString()}
                        </p>
                        <div className="mt-4 flex flex-wrap gap-x-4 gap-y-2 text-sm text-foreground/80">
                            {intake.email && (
                                <span className="inline-flex items-center gap-1">
                                    <Mail className="h-4 w-4 text-foreground/55" />
                                    <a
                                        href={`mailto:${intake.email}`}
                                        className="hover:text-terracotta"
                                    >
                                        {intake.email}
                                    </a>
                                </span>
                            )}
                            {intake.phone && (
                                <span className="inline-flex items-center gap-1">
                                    <Phone className="h-4 w-4 text-foreground/55" />
                                    <a
                                        href={`tel:${intake.phone}`}
                                        className="hover:text-terracotta"
                                    >
                                        {intake.phone}
                                    </a>
                                </span>
                            )}
                            {intake.age && (
                                <span className="inline-flex items-center gap-1">
                                    <Cake className="h-4 w-4 text-foreground/55" />
                                    {intake.age}
                                </span>
                            )}
                            {intake.occupation && (
                                <span className="inline-flex items-center gap-1">
                                    <Briefcase className="h-4 w-4 text-foreground/55" />
                                    {intake.occupation}
                                </span>
                            )}
                            {(intake.city || intake.state) && (
                                <span className="inline-flex items-center gap-1">
                                    <MapPin className="h-4 w-4 text-foreground/55" />
                                    {locationText}
                                </span>
                            )}
                        </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                        <label className="flex items-center gap-2 text-sm">
                            <span className="text-foreground/65">Status</span>
                            <select
                                value={doc.status}
                                onChange={(e) => updateStatus(e.target.value)}
                                disabled={savingStatus}
                                data-testid="admin-status-select"
                                className="rounded-xl bg-cream border border-border/80 px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage"
                            >
                                {STATUS_OPTIONS.map((o) => (
                                    <option key={o.value} value={o.value}>
                                        {o.label}
                                    </option>
                                ))}
                            </select>
                        </label>
                    </div>
                </div>
                <div className="mt-4 flex flex-wrap items-center gap-2 text-xs">
                    <span
                        className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 ${
                            ghl.status === "synced"
                                ? "bg-sage/20 text-moss"
                                : ghl.status === "failed"
                                  ? "bg-terracotta/15 text-terracotta"
                                  : "bg-sand text-foreground/65"
                        }`}
                    >
                        {ghl.status === "synced" ? (
                            <CheckCircle2 className="h-3.5 w-3.5" />
                        ) : ghl.status === "failed" ? (
                            <AlertTriangle className="h-3.5 w-3.5" />
                        ) : (
                            <Clock className="h-3.5 w-3.5" />
                        )}
                        GHL: {ghl.status || "pending"}
                        {ghl.contact_id && (
                            <span className="font-mono ml-1 text-foreground/55">
                                #{ghl.contact_id.slice(0, 8)}
                            </span>
                        )}
                    </span>
                    <button
                        type="button"
                        onClick={resyncGhl}
                        disabled={resync}
                        data-testid="admin-resync-ghl"
                        className="inline-flex items-center gap-1 rounded-full bg-cream border border-border/80 px-2.5 py-1 hover:border-moss/40"
                    >
                        {resync ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                            <RefreshCw className="h-3.5 w-3.5" />
                        )}
                        Re-sync to GHL
                    </button>
                </div>
            </div>

            <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* AI Summary column */}
                <div className="lg:col-span-2 space-y-4">
                    <div className="rounded-3xl border border-terracotta/30 bg-cream/80 p-6 sm:p-7">
                        <div className="flex items-center justify-between mb-3">
                            <h2 className="font-display text-2xl text-moss inline-flex items-center gap-2">
                                <Sparkles className="h-5 w-5 text-terracotta" />
                                AI clinical summary
                            </h2>
                            <button
                                type="button"
                                onClick={regenAi}
                                disabled={regen}
                                data-testid="admin-regenerate-ai"
                                className="inline-flex items-center gap-1 rounded-xl bg-white border border-border/80 px-3 py-1.5 text-xs text-moss hover:border-moss/40"
                            >
                                {regen ? (
                                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                ) : (
                                    <RefreshCw className="h-3.5 w-3.5" />
                                )}
                                Regenerate
                            </button>
                        </div>
                        {!doc.ai ? (
                            <p className="text-sm text-foreground/60">
                                AI summary not yet generated. Click “Regenerate” to create one.
                            </p>
                        ) : ai.error ? (
                            <div className="rounded-xl border border-terracotta/30 bg-terracotta/5 p-3 text-sm text-terracotta">
                                {ai.error}
                            </div>
                        ) : (
                            <div className="space-y-5">
                                {ai.client_snapshot && (
                                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                                        {Object.entries(ai.client_snapshot).map(
                                            ([k, v]) =>
                                                v && (
                                                    <div
                                                        key={k}
                                                        className="rounded-xl bg-white/70 border border-border/70 p-3"
                                                    >
                                                        <p className="text-foreground/55 uppercase tracking-wider font-accent">
                                                            {k.replaceAll("_", " ")}
                                                        </p>
                                                        <p className="mt-1 text-foreground/90 text-sm">
                                                            {v}
                                                        </p>
                                                    </div>
                                                ),
                                        )}
                                    </div>
                                )}

                                {ai.summary && (
                                    <div>
                                        <p className="font-accent text-xs text-terracotta">SUMMARY</p>
                                        <p className="mt-2 text-foreground/90 leading-relaxed whitespace-pre-wrap">
                                            {ai.summary}
                                        </p>
                                    </div>
                                )}

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    {ai.likely_constitution && (
                                        <div className="rounded-xl border border-border/70 bg-white/70 p-4">
                                            <p className="font-accent text-xs text-terracotta">
                                                LIKELY CONSTITUTION (PRAKRUTI)
                                            </p>
                                            <p className="mt-2 font-display text-lg text-moss">
                                                {ai.likely_constitution.dominant_dosha}
                                                {ai.likely_constitution.secondary_dosha && (
                                                    <span className="text-foreground/55 text-base">
                                                        {" "}
                                                        with{" "}
                                                        {ai.likely_constitution.secondary_dosha}
                                                    </span>
                                                )}
                                            </p>
                                            <p className="mt-2 text-xs text-foreground/70">
                                                {ai.likely_constitution.reasoning}
                                            </p>
                                        </div>
                                    )}
                                    {ai.likely_imbalance && (
                                        <div className="rounded-xl border border-border/70 bg-white/70 p-4">
                                            <p className="font-accent text-xs text-terracotta">
                                                LIKELY IMBALANCE (VIKRUTI)
                                            </p>
                                            <p className="mt-2 font-display text-lg text-moss">
                                                {ai.likely_imbalance.dominant_vikruti}
                                            </p>
                                            <p className="mt-2 text-xs text-foreground/70">
                                                {ai.likely_imbalance.reasoning}
                                            </p>
                                        </div>
                                    )}
                                </div>

                                {Array.isArray(ai.red_flags) && ai.red_flags.length > 0 && (
                                    <div className="rounded-xl border border-terracotta/40 bg-terracotta/10 p-4">
                                        <p className="font-accent text-xs text-terracotta inline-flex items-center gap-1">
                                            <AlertTriangle className="h-3.5 w-3.5" /> RED FLAGS
                                        </p>
                                        <ul className="mt-2 list-disc pl-5 text-sm text-foreground/85 space-y-1">
                                            {ai.red_flags.map((r, i) => (
                                                <li key={`rf-${i}-${String(r).slice(0, 24)}`}>{r}</li>
                                            ))}
                                        </ul>
                                    </div>
                                )}

                                {Array.isArray(ai.recommendations) &&
                                    ai.recommendations.length > 0 && (
                                        <div>
                                            <p className="font-accent text-xs text-terracotta">
                                                AI RECOMMENDATIONS (REVIEW & REFINE)
                                            </p>
                                            <div className="mt-3 space-y-2">
                                                {ai.recommendations.map((r, i) => {
                                                    const Icon =
                                                        CATEGORY_ICON[r.category] || Sparkles;
                                                    return (
                                                        <div
                                                            key={`rec-${i}-${(r.suggestion || "").slice(0, 24)}`}
                                                            data-testid="admin-ai-rec"
                                                            className="rounded-xl border border-border/70 bg-white/80 p-4 flex gap-3"
                                                        >
                                                            <Icon
                                                                className="h-5 w-5 text-moss shrink-0 mt-0.5"
                                                                strokeWidth={1.5}
                                                            />
                                                            <div className="flex-1">
                                                                <p className="text-xs font-accent text-terracotta">
                                                                    {r.category}
                                                                </p>
                                                                <p className="mt-0.5 text-sm font-medium text-foreground/90">
                                                                    {r.suggestion}
                                                                </p>
                                                                <p className="mt-1 text-xs text-foreground/65">
                                                                    {r.why}
                                                                </p>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}

                                {Array.isArray(ai.questions_to_ask) &&
                                    ai.questions_to_ask.length > 0 && (
                                        <div className="rounded-xl border border-border/70 bg-sand/30 p-4">
                                            <p className="font-accent text-xs text-terracotta">
                                                QUESTIONS FOR THE FIRST SESSION
                                            </p>
                                            <ul className="mt-2 list-decimal pl-5 text-sm text-foreground/85 space-y-1.5">
                                                {ai.questions_to_ask.map((q, i) => (
                                                    <li key={`q-${i}-${String(q).slice(0, 24)}`}>{q}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                <p className="text-xs text-foreground/50 mt-2">
                                    Generated by {ai.model || "AI"} • {ai.generated_at}
                                </p>
                            </div>
                        )}
                    </div>

                    {/* Full intake sections */}
                    <Section icon={FileText} title="Why they’re here">
                        <KV label="Primary concern" value={intake.primary_concern} />
                        <KV label="Why now" value={intake.why_seeking_care} />
                        <KV label="Goals" value={intake.goals} />
                        <KV label="How heard" value={intake.how_heard} />
                    </Section>

                    <Section icon={Users} title="Demographics & contact" defaultOpen={false}>
                        <SubObject obj={demographicsObj} />
                        {intake.emergency_contact && (
                            <>
                                <p className="mt-4 font-accent text-xs text-terracotta">
                                    EMERGENCY CONTACT
                                </p>
                                <SubObject obj={intake.emergency_contact} />
                            </>
                        )}
                    </Section>

                    <Section icon={HeartPulse} title="Medical & family history" defaultOpen={false}>
                        <SubObject obj={intake.medical_history || {}} />
                    </Section>

                    <Section icon={Activity} title="Substances, routines & relationships" defaultOpen={false}>
                        <p className="font-accent text-xs text-terracotta">SUBSTANCE USE</p>
                        <SubObject obj={intake.substance_use || {}} />
                        <p className="mt-4 font-accent text-xs text-terracotta">PRACTICES</p>
                        <SubObject obj={intake.regular_practices || {}} />
                        <p className="mt-4 font-accent text-xs text-terracotta">RELATIONSHIPS</p>
                        <SubObject obj={intake.relationship_life || {}} />
                    </Section>

                    <Section icon={Apple} title="Diet & daily rhythm" defaultOpen={false}>
                        <p className="font-accent text-xs text-terracotta">DIET</p>
                        <SubObject obj={intake.diet || {}} />
                        <p className="mt-4 font-accent text-xs text-terracotta">DAILY SCHEDULE</p>
                        <SubObject obj={intake.daily_schedule || {}} />
                    </Section>

                    <Section icon={Pill} title="Allergies & medications" defaultOpen={false}>
                        <p className="font-accent text-xs text-terracotta">ALLERGIES</p>
                        <SubObject obj={intake.allergies || {}} />
                        <p className="mt-4 font-accent text-xs text-terracotta">MEDICATIONS</p>
                        <TableList
                            rows={intake.medications || []}
                            cols={MEDICATIONS_COLS}
                        />
                    </Section>

                    <Section icon={Sparkles} title="Ayurvedic constitution (dosha)" defaultOpen={false}>
                        <SubObject obj={intake.dosha_assessment || {}} />
                    </Section>

                    <Section icon={Brain} title="Mental, emotional & challenges" defaultOpen={false}>
                        <p className="font-accent text-xs text-terracotta">MENTAL / EMOTIONAL</p>
                        <SubObject obj={intake.mental_emotional || {}} />
                        {intake.womens_health && (
                            <>
                                <p className="mt-4 font-accent text-xs text-terracotta">
                                    WOMEN&rsquo;S HEALTH
                                </p>
                                <SubObject obj={intake.womens_health} />
                            </>
                        )}
                        <p className="mt-4 font-accent text-xs text-terracotta">
                            CHALLENGING PATTERNS
                        </p>
                        {CHALLENGING_GROUPS.map(([t, k]) => (
                            <div key={k} className="mt-3">
                                <p className="text-xs text-foreground/65 font-medium">{t}</p>
                                <TableList
                                    rows={intake.challenging_patterns?.[k] || []}
                                    cols={SYMPTOM_COLS}
                                />
                            </div>
                        ))}
                    </Section>

                    <Section icon={FileText} title="Consents & signatures" defaultOpen={false}>
                        <SubObject obj={intake.consent || {}} />
                    </Section>

                    {intake.additional_notes && (
                        <Section icon={StickyNote} title="Additional notes from client">
                            <p className="whitespace-pre-wrap">{intake.additional_notes}</p>
                        </Section>
                    )}
                </div>

                {/* Riley's notes column */}
                <div className="space-y-4">
                    <div className="rounded-3xl border border-border/80 bg-white/95 p-6">
                        <h2 className="font-display text-xl text-moss inline-flex items-center gap-2">
                            <StickyNote className="h-5 w-5 text-moss" /> Your notes
                        </h2>
                        <p className="mt-1 text-xs text-foreground/55">
                            Private — only you can see these.
                        </p>
                        <textarea
                            value={noteText}
                            onChange={(e) => setNoteText(e.target.value)}
                            rows={5}
                            data-testid="admin-note-input"
                            placeholder="What did you notice? What do you want to ask in the first session?"
                            className="mt-3 w-full rounded-xl bg-cream border border-border/80 px-3 py-2.5 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage"
                        />
                        <button
                            type="button"
                            onClick={addNote}
                            disabled={adding}
                            data-testid="admin-add-note"
                            className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded-xl bg-moss text-cream px-4 py-2.5 text-sm font-medium hover:bg-moss/90 disabled:opacity-60"
                        >
                            {adding ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                                <Save className="h-4 w-4" />
                            )}
                            Save note
                        </button>
                        <div className="mt-5 space-y-3 max-h-[60vh] overflow-y-auto pr-1">
                            {notes.length === 0 ? (
                                <p className="text-xs text-foreground/50">No notes yet.</p>
                            ) : (
                                notesNewestFirst.map((n) => (
                                    <div
                                        key={n.id || `${n.created_at}-${n.author}`}
                                        data-testid="admin-note-entry"
                                        className="rounded-xl border border-border/70 bg-sand/40 p-3"
                                    >
                                        <p className="text-xs text-foreground/55">
                                            {new Date(n.created_at).toLocaleString()}
                                        </p>
                                        <p className="mt-1 text-sm text-foreground/90 whitespace-pre-wrap">
                                            {n.note}
                                        </p>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
